import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { ArrowLeft, Share2, Download, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AmbientBackground from "../components/AmbientBackground";
import { VerseArt } from "../components/VerseArt";
import { getVOTD } from "../api";

const REF_TO_PATH = (ref) => {
  // best-effort link into the reader (book name + chapter)
  const m = ref.match(/^(.*)\s+(\d+):/);
  return m ? { name: m[1], chapter: m[2] } : null;
};

export default function VerseOfDayPage() {
  const navigate = useNavigate();
  const [votd, setVotd] = useState(null);
  const [saving, setSaving] = useState(false);
  const artRef = useRef(null);

  useEffect(() => {
    getVOTD().then(setVotd).catch(() => setVotd({ error: true }));
  }, []);

  // API.Bible FUMS: report each view of licensed scripture
  useEffect(() => {
    if (votd?.fums_token && window.fums) {
      try { window.fums("trackView", votd.fums_token); } catch { /* noop */ }
    }
  }, [votd]);

  const share = async () => {
    const text = `“${votd.verse_text}” — ${votd.verse_ref}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Verse of the Day", text }); } catch (e) { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(text); toast.success("Verse copied to clipboard"); }
      catch { toast.error("Couldn't copy"); }
    }
  };

  const save = async () => {
    if (!artRef.current) return;
    setSaving(true);
    try {
      const dataUrl = await toPng(artRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `verseflow-${votd.verse_ref.replace(/[:\s]/g, "-")}.png`;
      a.click();
      toast.success("Image saved");
    } catch (e) {
      toast.error("Couldn't save the image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <AmbientBackground accent={{ a: "#E0B85C", b: "#5C8A6E", c: "#2E5A66" }} />
      <button
        onClick={() => navigate("/")}
        data-testid="votd-back"
        className="font-ui fixed left-4 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/70 backdrop-blur-md transition-colors hover:bg-muted"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-20">
        {!votd ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        ) : votd.error || !votd.verse_text ? (
          <p className="font-ui text-sm text-muted-foreground">Couldn’t load today’s verse.</p>
        ) : (
          <>
            <p className="font-ui mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-primary">
              Verse of the Day
            </p>
            <VerseArt
              ref={artRef}
              votd={votd}
              className="aspect-[3/4] w-full rounded-3xl border border-border shadow-2xl"
            />
            <div className="mt-6 flex w-full items-center gap-3">
              <button
                onClick={share}
                data-testid="votd-share"
                className="font-ui flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="votd-save"
                className="font-ui flex flex-1 items-center justify-center gap-2 rounded-full border border-border py-3.5 text-sm font-semibold transition-colors hover:bg-muted disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Save
              </button>
            </div>
            {(() => {
              const p = REF_TO_PATH(votd.verse_ref);
              return p ? (
                <button
                  onClick={() => navigate("/")}
                  data-testid="votd-read"
                  className="font-ui mt-4 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <BookOpen className="h-4 w-4" /> {votd.verse_ref}
                </button>
              ) : null;
            })()}
          </>
        )}
      </div>
    </div>
  );
}
